/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  Box, Typography, Button, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, IconButton, Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import '../../styles/ManageProgramData.css';

const formatTime = (dateString) => {
  if (!dateString) return '';
  const d = new Date(dateString);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
};

const AdminChats = ({ user }) => {
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [facilityUsers, setFacilityUsers] = useState([]);
  const [institutionRoles, setInstitutionRoles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingChat, setEditingChat] = useState(null);
  const [dialogForm, setDialogForm] = useState({
    name: '',
    participants: [],
    chat_admins: []
  });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [chatToDelete, setChatToDelete] = useState(null);
  const [dialogSearch, setDialogSearch] = useState('');
  const [dialogRoleFilter, setDialogRoleFilter] = useState('');
  const [groupInfoOpen, setGroupInfoOpen] = useState(false);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const requestedChatId = searchParams.get('chat');

  const userId = user?._id || user?.id;
  const adminFacilityId = user?.facility?._id || user?.facility;

  const fetchChats = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/facility-chats');
      setChats(response.data);
      setSelectedChat(prev => {
        if (!prev) return null;
        const updated = response.data.find(c => c._id === prev._id);
        return updated || null;
      });
    } catch (error) {
      console.error('Error fetching chats:', error);
      toast.error('Failed to load chats');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFacilityUsers = useCallback(async () => {
    if (!adminFacilityId) return;
    try {
      const response = await axios.get(`/api/users?facility=${adminFacilityId}`);
      setFacilityUsers(response.data.filter(u => u.status === 'ACTIVE'));
    } catch (error) {
      console.error('Error fetching facility users:', error);
    }
  }, [adminFacilityId]);

  const fetchMessages = useCallback(async (chatId, before = null) => {
    try {
      if (!before) setMessagesLoading(true);
      const params = new URLSearchParams({ limit: '50' });
      if (before) params.set('before', before);

      const response = await axios.get(`/api/facility-chats/${chatId}/messages?${params}`);
      const data = response.data;

      if (before) {
        setMessages(prev => [...data.messages, ...prev]);
      } else {
        setMessages(data.messages);
      }
      setHasMore(data.hasMore);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setMessagesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchChats();
    fetchFacilityUsers();
    axios.get('/api/institution-roles')
      .then(res => setInstitutionRoles(res.data || []))
      .catch(err => console.error('Error fetching institution roles:', err));
  }, [fetchChats, fetchFacilityUsers]);

  // Arrived from a link that names a chat — "Open in Chats" on the home
  // dashboard, for instance. Open it once the list has loaded, then drop the
  // parameter so it can't override what the user clicks next.
  useEffect(() => {
    if (!requestedChatId || chats.length === 0) return;
    const target = chats.find(c => c._id === requestedChatId);
    if (target) setSelectedChat(target);
    setSearchParams({}, { replace: true });
  }, [requestedChatId, chats, setSearchParams]);

  useEffect(() => {
    if (selectedChat) {
      fetchMessages(selectedChat._id);
      axios.patch(`/api/facility-chats/${selectedChat._id}/read`).catch(() => {});
    }
  }, [selectedChat, fetchMessages]);

  useEffect(() => {
    if (!messagesLoading && messages.length > 0) {
      const container = messagesContainerRef.current;
      if (container) {
        requestAnimationFrame(() => {
          container.scrollTop = container.scrollHeight;
        });
      }
    }
  }, [messages, messagesLoading]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedChat || submitting) return;
    try {
      setSubmitting(true);
      await axios.post(`/api/facility-chats/${selectedChat._id}/messages`, {
        content: newMessage.trim()
      });
      setNewMessage('');
      await fetchMessages(selectedChat._id);
      fetchChats();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to send message');
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleLoadMore = () => {
    if (messages.length > 0 && hasMore) {
      fetchMessages(selectedChat._id, messages[0].createdAt);
    }
  };

  const isChatAdminOfSelected = (chat) => {
    if (!chat) return false;
    return chat.participants?.some(
      p => (p.user?._id || p.user)?.toString() === userId && p.role === 'chat_admin'
    );
  };

  const openCreateDialog = () => {
    setEditingChat(null);
    setDialogForm({ name: '', participants: [], chat_admins: [] });
    setDialogSearch('');
    setDialogRoleFilter('');
    setDialogOpen(true);
  };

  const openEditDialog = (chat) => {
    setEditingChat(chat);
    const participantIds = chat.participants.map(p => p.user?._id || p.user);
    const chatAdminIds = chat.participants
      .filter(p => p.role === 'chat_admin')
      .map(p => p.user?._id || p.user);
    setDialogForm({
      name: chat.name,
      participants: participantIds,
      chat_admins: chatAdminIds
    });
    setDialogSearch('');
    setDialogRoleFilter('');
    setDialogOpen(true);
  };

  const handleDialogSave = async () => {
    if (!dialogForm.name.trim()) {
      toast.error('Chat name is required');
      return;
    }
    if (dialogForm.participants.length < 2) {
      toast.error('At least 2 participants are required');
      return;
    }

    const creatorIsParticipant = dialogForm.participants.some(
      id => id?.toString() === userId
    );
    if (!creatorIsParticipant && dialogForm.chat_admins.length === 0) {
      toast.error('At least one chat admin is required when you are not a participant');
      return;
    }

    try {
      if (editingChat) {
        await axios.put(`/api/facility-chats/${editingChat._id}`, {
          name: dialogForm.name,
          participants: dialogForm.participants,
          chat_admins: dialogForm.chat_admins
        });
        toast.success('Chat updated');
      } else {
        await axios.post('/api/facility-chats', {
          name: dialogForm.name,
          participants: dialogForm.participants,
          chat_admins: dialogForm.chat_admins
        });
        toast.success('Chat created');
      }
      setDialogOpen(false);
      fetchChats();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save chat');
    }
  };

  const handleDeleteChat = async () => {
    if (!chatToDelete) return;
    try {
      await axios.delete(`/api/facility-chats/${chatToDelete._id}`);
      toast.success('Chat deleted');
      if (selectedChat?._id === chatToDelete._id) {
        setSelectedChat(null);
        setMessages([]);
      }
      setDeleteConfirmOpen(false);
      setChatToDelete(null);
      fetchChats();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete chat');
    }
  };

  const toggleNotifications = async (chat) => {
    const participant = chat.participants?.find(p =>
      (p.user?._id || p.user)?.toString() === userId
    );
    const currentState = participant?.notifications_enabled ?? true;
    try {
      await axios.patch(`/api/facility-chats/${chat._id}/notifications`, { enabled: !currentState });
      setChats(prev => prev.map(c => {
        if (c._id !== chat._id) return c;
        return {
          ...c,
          participants: c.participants.map(p =>
            (p.user?._id || p.user)?.toString() === userId
              ? { ...p, notifications_enabled: !currentState }
              : p
          )
        };
      }));
      if (selectedChat?._id === chat._id) {
        setSelectedChat(prev => ({
          ...prev,
          participants: prev.participants.map(p =>
            (p.user?._id || p.user)?.toString() === userId
              ? { ...p, notifications_enabled: !currentState }
              : p
          )
        }));
      }
      toast.success(currentState ? 'Notifications muted' : 'Notifications enabled');
    } catch {
      toast.error('Failed to update notifications');
    }
  };

  const getMyNotifState = (chat) => {
    const participant = chat?.participants?.find(p =>
      (p.user?._id || p.user)?.toString() === userId
    );
    return participant?.notifications_enabled ?? true;
  };

  const selectedParticipantObjects = facilityUsers.filter(u =>
    dialogForm.participants.includes(u._id)
  );

  const filtered = chats.filter(c =>
    !searchTerm || c.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const creatorIsParticipant = dialogForm.participants.some(
    id => id?.toString() === userId
  );

  const dialogFilteredUsers = facilityUsers.filter(u => {
    if (dialogForm.participants.includes(u._id)) return false;
    if (!dialogSearch.trim()) return true;
    const term = dialogSearch.toLowerCase();
    return (
      `${u.first_name} ${u.last_name}`.toLowerCase().includes(term) ||
      u.email?.toLowerCase().includes(term)
    );
  });

  const getRoleName = (r) => {
    if (typeof r === 'string' && r.length < 30) return r;
    if (r && typeof r === 'object' && r.role) return r.role;
    return null;
  };

  const addParticipant = (uid) => {
    setDialogForm(prev => ({
      ...prev,
      participants: [...prev.participants, uid]
    }));
    setDialogSearch('');
  };

  const removeParticipant = (uid) => {
    setDialogForm(prev => ({
      ...prev,
      participants: prev.participants.filter(id => id !== uid),
      chat_admins: prev.chat_admins.filter(id => id !== uid)
    }));
  };

  const toggleChatAdmin = (uid) => {
    setDialogForm(prev => {
      const isAdmin = prev.chat_admins.includes(uid);
      return {
        ...prev,
        chat_admins: isAdmin
          ? prev.chat_admins.filter(id => id !== uid)
          : [...prev.chat_admins, uid]
      };
    });
  };

  return (
    <div className="admin-page-container" style={{ display: 'flex', height: 'calc(100vh - 60px)' }}>
      {/* Left Panel: Chat List */}
      <div style={{
        width: '320px', minWidth: '280px', borderRight: '1px solid #e0e0e0',
        display: 'flex', flexDirection: 'column', background: '#fafbfc'
      }}>
        <div style={{ padding: '16px', borderBottom: '1px solid #e0e0e0' }}>
          <Button variant="contained" size="small" startIcon={<AddIcon />}
            onClick={openCreateDialog} fullWidth sx={{ mb: 1 }}>
            Create New Chat
          </Button>
          <TextField size="small" placeholder="Search chats..." fullWidth
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
          />
        </div>

        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <Typography sx={{ p: 2, color: '#999', textAlign: 'center' }}>Loading...</Typography>
          ) : filtered.length === 0 ? (
            <Typography sx={{ p: 2, color: '#999', textAlign: 'center' }}>No chats found</Typography>
          ) : (
            filtered.map(chat => (
              <div key={chat._id}
                onClick={() => { setSelectedChat(chat); setGroupInfoOpen(false); }}
                style={{
                  padding: '10px 16px', cursor: 'pointer',
                  background: selectedChat?._id === chat._id ? '#e3f2fd' : 'transparent',
                  borderBottom: '1px solid #f0f0f0',
                  transition: 'background 0.15s'
                }}
                onMouseEnter={e => { if (selectedChat?._id !== chat._id) e.currentTarget.style.background = 'var(--brand-primary-softer, #f5f5f5)'; }}
                onMouseLeave={e => { if (selectedChat?._id !== chat._id) e.currentTarget.style.background = 'transparent'; }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                    {chat.name}
                  </Typography>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {chat.unreadCount > 0 && (
                      <span style={{
                        background: 'var(--brand-primary, #c41e3a)', color: '#fff', borderRadius: '10px',
                        padding: '1px 6px', fontSize: '0.65rem', fontWeight: 600
                      }}>
                        {chat.unreadCount}
                      </span>
                    )}
                    <Typography variant="caption" sx={{ color: '#999', fontSize: '0.7rem' }}>
                      {formatTime(chat.last_message_at)}
                    </Typography>
                  </div>
                </div>
                <Typography variant="caption" sx={{ color: '#888', display: 'block', mt: 0.3 }}>
                  {chat.participants?.length || 0} participants
                </Typography>
                {chat.last_message_preview && (
                  <Typography variant="caption" sx={{ color: '#aaa', display: 'block', mt: 0.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {chat.last_message_preview}
                  </Typography>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Right Panel: Messages + Group Info */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'row' }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {!selectedChat ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, color: '#999' }}>
              <Typography>Select a chat to view messages</Typography>
            </Box>
          ) : (
            <>
              {/* Chat Header */}
              <div style={{
                padding: '12px 20px', borderBottom: '1px solid #e0e0e0',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff'
              }}>
                <div>
                  <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                    {selectedChat.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#888' }}>
                    {selectedChat.facility?.facility_name} &middot; {selectedChat.participants?.length} participants
                  </Typography>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                  <Tooltip title={getMyNotifState(selectedChat) ? 'Mute notifications' : 'Unmute notifications'}>
                    <IconButton size="small" onClick={() => toggleNotifications(selectedChat)}>
                      {getMyNotifState(selectedChat)
                        ? <NotificationsIcon fontSize="small" />
                        : <NotificationsOffIcon fontSize="small" sx={{ color: '#999' }} />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Group info">
                    <IconButton size="small" onClick={() => setGroupInfoOpen(prev => !prev)}
                      sx={{ color: groupInfoOpen ? '#1976d2' : 'inherit' }}>
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>

              {/* Messages Area */}
              <div ref={messagesContainerRef} style={{
                flex: 1, overflowY: 'auto', padding: '12px 20px',
                display: 'flex', flexDirection: 'column', gap: '8px'
              }}>
                {messagesLoading ? (
                  <Typography sx={{ textAlign: 'center', color: '#999', py: 4 }}>Loading messages...</Typography>
                ) : (
                  <>
                    {hasMore && (
                      <Button size="small" onClick={handleLoadMore} sx={{ alignSelf: 'center', mb: 1 }}>
                        Load older messages
                      </Button>
                    )}
                    {messages.length === 0 ? (
                      <Typography sx={{ textAlign: 'center', color: '#999', py: 4 }}>No messages yet</Typography>
                    ) : (
                      messages.map(msg => {
                        const isMine = msg.sender === userId;
                        return (
                          <div key={msg._id} style={{
                            display: 'flex', gap: '8px',
                            flexDirection: isMine ? 'row-reverse' : 'row',
                            justifyContent: 'flex-start',
                            alignItems: 'flex-start'
                          }}>
                            <div style={{
                              width: '32px', height: '32px', borderRadius: '50%',
                              background: isMine ? '#1976d2' : '#e0e0e0',
                              color: isMine ? '#fff' : '#333',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '0.7rem', fontWeight: 600, flexShrink: 0
                            }}>
                              {getInitials(msg.sender_name)}
                            </div>
                            <div style={{
                              maxWidth: '70%', padding: '8px 12px', borderRadius: '12px',
                              background: isMine ? '#e3f2fd' : '#f5f5f5'
                            }}>
                              {!isMine && (
                                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.3 }}>
                                  {msg.sender_name}
                                </Typography>
                              )}
                              <Typography variant="body2" sx={{ fontSize: '0.85rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {msg.content}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 0.3, textAlign: isMine ? 'right' : 'left' }}>
                                {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </Typography>
                            </div>
                          </div>
                        );
                      })
                    )}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>

              {/* Message Input */}
              <div style={{
                padding: '12px 20px', borderTop: '1px solid #e0e0e0',
                display: 'flex', gap: '8px', background: '#fff'
              }}>
                <textarea
                  value={newMessage}
                  onChange={e => setNewMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type a message... (Enter to send)"
                  rows="2"
                  disabled={submitting}
                  style={{
                    flex: 1, resize: 'none', border: '1px solid #ddd', borderRadius: '8px',
                    padding: '8px 12px', fontFamily: 'inherit', fontSize: '0.85rem', outline: 'none'
                  }}
                />
                <Button variant="contained" onClick={handleSendMessage}
                  disabled={!newMessage.trim() || submitting}
                  sx={{ minWidth: '60px', alignSelf: 'flex-end' }}>
                  Send
                </Button>
              </div>
            </>
          )}
        </div>

        {/* Group Info Side Panel */}
        {groupInfoOpen && selectedChat && (
          <div style={{
            width: '300px', borderLeft: '1px solid #e0e0e0', background: '#fafbfc',
            display: 'flex', flexDirection: 'column', overflowY: 'auto'
          }}>
            <div style={{
              padding: '12px 16px', borderBottom: '1px solid #e0e0e0',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                Group Info
              </Typography>
              <IconButton aria-label="Close" size="small" onClick={() => setGroupInfoOpen(false)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>

            <div style={{ padding: '16px' }}>
              <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Chat Name
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 500 }}>
                {selectedChat.name}
              </Typography>

              {selectedChat.facility?.facility_name && (
                <div style={{ marginTop: '12px' }}>
                  <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Facility
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {selectedChat.facility.facility_name}
                  </Typography>
                </div>
              )}

              <div style={{ marginTop: '16px' }}>
                <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Members ({selectedChat.participants?.length || 0})
                </Typography>

                <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {(selectedChat.participants || [])
                    .slice()
                    .sort((a, b) => {
                      if (a.role === 'chat_admin' && b.role !== 'chat_admin') return -1;
                      if (a.role !== 'chat_admin' && b.role === 'chat_admin') return 1;
                      return 0;
                    })
                    .map(p => {
                      const pUser = p.user || {};
                      const name = `${pUser.first_name || ''} ${pUser.last_name || ''}`.trim() || 'Unknown';
                      return (
                        <div key={pUser._id || p.user} style={{
                          display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '4px 0'
                        }}>
                          <div style={{
                            width: '28px', height: '28px', borderRadius: '50%',
                            background: p.role === 'chat_admin' ? '#1976d2' : '#e0e0e0',
                            color: p.role === 'chat_admin' ? '#fff' : '#333',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '0.6rem', fontWeight: 600, flexShrink: 0,
                            marginTop: '1px'
                          }}>
                            {getInitials(name)}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 500, lineHeight: 1.3, textAlign: 'left' }}>
                              {name}
                            </Typography>
                            {pUser.email && (
                              <Typography variant="caption" sx={{ color: '#999', display: 'block', lineHeight: 1.2, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {pUser.email}
                              </Typography>
                            )}
                          </div>
                          {p.role === 'chat_admin' && (
                            <span style={{
                              fontSize: '0.6rem', padding: '1px 6px', borderRadius: '3px',
                              background: '#e3f2fd', color: '#1976d2', fontWeight: 600, flexShrink: 0,
                              marginTop: '2px'
                            }}>
                              Admin
                            </span>
                          )}
                        </div>
                      );
                    })
                  }
                </div>
              </div>

              {/* Notification status */}
              <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #eee' }}>
                <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Notifications
                </Typography>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
                  {getMyNotifState(selectedChat)
                    ? <NotificationsIcon sx={{ fontSize: '16px', color: '#1976d2' }} />
                    : <NotificationsOffIcon sx={{ fontSize: '16px', color: '#999' }} />}
                  <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>
                    {getMyNotifState(selectedChat) ? 'Enabled' : 'Muted'}
                  </Typography>
                  <Button size="small" variant="text" sx={{ ml: 'auto', fontSize: '0.72rem', textTransform: 'none' }}
                    onClick={() => toggleNotifications(selectedChat)}>
                    {getMyNotifState(selectedChat) ? 'Mute' : 'Unmute'}
                  </Button>
                </div>
              </div>

              {/* Chat admin actions */}
              {isChatAdminOfSelected(selectedChat) && (
                <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #eee', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <Button variant="outlined" size="small" startIcon={<EditIcon />}
                    onClick={() => { setGroupInfoOpen(false); openEditDialog(selectedChat); }}
                    sx={{ textTransform: 'none', justifyContent: 'flex-start', fontSize: '0.78rem' }}>
                    Edit Name, Members & Roles
                  </Button>
                  <Button variant="outlined" size="small" color="error" startIcon={<DeleteIcon />}
                    onClick={() => { setChatToDelete(selectedChat); setDeleteConfirmOpen(true); }}
                    sx={{ textTransform: 'none', justifyContent: 'flex-start' }}>
                    Delete Chat
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { maxHeight: '85vh' } }}>
        <DialogTitle>{editingChat ? 'Edit Chat' : 'Create New Chat'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', pb: 0 }}>
          <TextField label="Chat Name" fullWidth margin="normal"
            value={dialogForm.name} onChange={e => setDialogForm({ ...dialogForm, name: e.target.value })}
            sx={{ flexShrink: 0 }}
          />

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, flexShrink: 0 }}>
            Select Participants
          </Typography>
          <div style={{ display: 'flex', gap: '8px', flexShrink: 0, marginBottom: '8px', alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search by name or email..."
              value={dialogSearch}
              onChange={e => setDialogSearch(e.target.value)}
              autoComplete="off"
              sx={{ flex: 1 }}
            />
            <select
              value={dialogRoleFilter}
              onChange={e => setDialogRoleFilter(e.target.value)}
              style={{
                padding: '7px 8px', borderRadius: '6px', border: '1px solid #ccc',
                fontSize: '0.78rem', background: '#fff', cursor: 'pointer',
                width: '120px', flexShrink: 0
              }}
            >
              <option value="">All Roles</option>
              {institutionRoles
                .map(r => r.role || r.name || '')
                .filter(Boolean)
                .sort()
                .map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
            </select>
          </div>

          <div style={{
            flex: 1, overflowY: 'auto', border: '1px solid #e0e0e0',
            borderRadius: '8px', minHeight: '150px'
          }}>
            {facilityUsers.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                No users found in your facility
              </div>
            ) : (
              (() => {
                const term = dialogSearch.toLowerCase().trim();
                const usersToShow = facilityUsers.filter(u => {
                  if (term) {
                    const match = `${u.first_name} ${u.last_name}`.toLowerCase().includes(term) ||
                      u.email?.toLowerCase().includes(term);
                    if (!match) return false;
                  }
                  if (dialogRoleFilter) {
                    const hasRole = (u.institution_roles || []).some(r =>
                      getRoleName(r) === dialogRoleFilter
                    );
                    if (!hasRole) return false;
                  }
                  return true;
                });
                if (usersToShow.length === 0) {
                  return (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                      No matching users found
                    </div>
                  );
                }
                return usersToShow.map((u, idx) => {
                  const isSelected = dialogForm.participants.includes(u._id);
                  const isChatAdmin = dialogForm.chat_admins.includes(u._id);
                  const roles = (u.institution_roles || []).map(r => getRoleName(r)).filter(Boolean);
                  return (
                    <div key={u._id} style={{
                      padding: '7px 12px',
                      display: 'flex', alignItems: 'flex-start', gap: '10px',
                      borderBottom: idx < usersToShow.length - 1 ? '1px solid #f0f0f0' : 'none',
                      background: isSelected ? '#f0f7ff' : 'transparent',
                      cursor: 'pointer',
                      transition: 'background 0.12s'
                    }}
                      onClick={() => isSelected ? removeParticipant(u._id) : addParticipant(u._id)}
                      onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'var(--brand-primary-softer, #fafafa)'; }}
                      onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                    >
                      <div style={{
                        width: '18px', height: '18px', borderRadius: '4px',
                        border: isSelected ? '2px solid #1976d2' : '2px solid #ccc',
                        background: isSelected ? '#1976d2' : '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0, transition: 'all 0.12s',
                        marginTop: '6px'
                      }}>
                        {isSelected && (
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path d="M2 6L5 9L10 3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </div>

                      <div style={{
                        width: '30px', height: '30px', borderRadius: '50%',
                        background: isSelected ? '#1976d2' : '#e8e8e8',
                        color: isSelected ? '#fff' : '#666',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.65rem', fontWeight: 600, flexShrink: 0,
                        transition: 'all 0.12s',
                        marginTop: '2px'
                      }}>
                        {getInitials(`${u.first_name} ${u.last_name}`)}
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.84rem', fontWeight: 500, lineHeight: 1.4, textAlign: 'left' }}>
                          {u.first_name} {u.last_name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#999', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.3, textAlign: 'left' }}>
                          {u.email}
                        </Typography>
                        {roles.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', marginTop: '2px' }}>
                            {roles.map(r => (
                              <span key={r} style={{
                                fontSize: '0.65rem', padding: '1px 6px', borderRadius: '3px',
                                background: '#eef0f2', color: '#555', fontWeight: 500
                              }}>
                                {r}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      {isSelected && (
                        <Tooltip title={isChatAdmin ? 'Remove admin role' : 'Make chat admin'} arrow>
                          <div
                            onClick={(e) => { e.stopPropagation(); toggleChatAdmin(u._id); }}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '4px',
                              padding: '3px 8px', borderRadius: '4px', cursor: 'pointer',
                              border: isChatAdmin ? '1.5px solid #1976d2' : '1.5px solid #ddd',
                              background: isChatAdmin ? '#e3f2fd' : '#fff',
                              transition: 'all 0.12s', flexShrink: 0,
                              marginTop: '4px'
                            }}
                          >
                            <div style={{
                              width: '14px', height: '14px', borderRadius: '3px',
                              border: isChatAdmin ? '2px solid #1976d2' : '2px solid #bbb',
                              background: isChatAdmin ? '#1976d2' : '#fff',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              transition: 'all 0.12s'
                            }}>
                              {isChatAdmin && (
                                <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                                  <path d="M1 4L3 6L7 2" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              )}
                            </div>
                            <span style={{
                              fontSize: '0.72rem', fontWeight: 600,
                              color: isChatAdmin ? '#1976d2' : '#999',
                              transition: 'color 0.12s'
                            }}>
                              Admin
                            </span>
                          </div>
                        </Tooltip>
                      )}
                    </div>
                  );
                });
              })()
            )}
          </div>

          {selectedParticipantObjects.length > 0 && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '8px 0 4px', flexShrink: 0
            }}>
              <Typography variant="caption" sx={{ color: '#555' }}>
                {selectedParticipantObjects.length} participant{selectedParticipantObjects.length !== 1 ? 's' : ''} selected
              </Typography>
              <Typography variant="caption" sx={{ color: creatorIsParticipant || dialogForm.chat_admins.length > 0 ? '#888' : '#d32f2f' }}>
                {creatorIsParticipant
                  ? 'You will be auto-set as admin'
                  : dialogForm.chat_admins.length === 0
                    ? 'Select at least one admin'
                    : `${dialogForm.chat_admins.length} admin(s) selected`}
              </Typography>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleDialogSave}>
            {editingChat ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Chat</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete &quot;{chatToDelete?.name}&quot;? This will permanently delete all messages in this chat.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteChat}>Delete</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AdminChats;
